// Root component: AntD theme + Router + QueryClient + auth hydrate.

import { useEffect, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { App as AntApp, ConfigProvider, Spin } from 'antd';
import viVN from 'antd/locale/vi_VN';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/query-client';
import { ROUTES } from './config/routes';
import { useAuth } from './features/auth/useAuth';
import { ProtectedRoute } from './features/auth/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { CustomersListPage } from './pages/CustomersListPage';
import { LicensesListPage } from './pages/LicensesListPage';
import { UsersListPage } from './pages/UsersListPage';
import { HelpPage } from './pages/HelpPage';
import { AppLayout } from './components/layout/AppLayout';

const theme = {
  token: {
    colorPrimary: '#722ed1',
    borderRadius: 6,
  },
};

function BootGate({ children }: { children: ReactNode }) {
  const { hydrate, isHydrated } = useAuth();
  useEffect(() => {
    void hydrate();
  }, [hydrate]);
  if (!isHydrated) {
    return (
      <div style={{ height: '100vh', display: 'grid', placeItems: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }
  return <>{children}</>;
}

/** Render children only for admin role; managers are bounced to dashboard. */
function AdminOnly({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user?.role !== 'admin') return <Navigate to={ROUTES.dashboard} replace />;
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path={ROUTES.login} element={<LoginPage />} />
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path={ROUTES.dashboard} element={<DashboardPage />} />
        <Route path={ROUTES.customers} element={<CustomersListPage />} />
        <Route path={ROUTES.licenses} element={<LicensesListPage />} />
        <Route
          path={ROUTES.users}
          element={
            <AdminOnly>
              <UsersListPage />
            </AdminOnly>
          }
        />
        <Route path={ROUTES.help} element={<HelpPage />} />
      </Route>
      <Route path="*" element={<Navigate to={ROUTES.dashboard} replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <ConfigProvider theme={theme} locale={viVN}>
        <AntApp>
          <QueryClientProvider client={queryClient}>
            <BootGate>
              <AppRoutes />
            </BootGate>
          </QueryClientProvider>
        </AntApp>
      </ConfigProvider>
    </BrowserRouter>
  );
}
